import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import ResponsiveSidebar from '../components/layout/ResponsiveSidebar';
import { 
  UserIcon, 
  FileIcon, 
  BarChartIcon, 
  LocationIcon,
  PhoneIcon,
  EmailIcon,
  MenuIcon,
  CloseIcon
} from '../components/icons/Icons';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user, signOut } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    fullName: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would call an API to update the user profile
    console.log('Profile updated:', formData);
    setIsEditing(false);
    // Show success message
    alert('Profile updated successfully!');
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:bg-gray-100"
              >
                {sidebarOpen ? <CloseIcon className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
              </button>
              <div className="flex items-center space-x-3">
                <img src="/hero.png" alt="Logo" className="w-8 h-8" />
                <h1 className="text-xl font-semibold text-gray-900">Profile</h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">{user?.full_name || user?.email}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <button
                onClick={handleSignOut}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                {t('auth.logout')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        <ResponsiveSidebar 
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          userRole="citizen"
          currentPage="profile"
        />

        {/* Main Content */}
        <main className="flex-1 p-4 sm:p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Page Header */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">My Profile</h2>
                  <p className="text-gray-600 text-sm sm:text-base mt-1">Manage your personal information and account settings</p>
                </div>
                <div className="sm:mt-0">
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base"
                  >
                    {isEditing ? 'Cancel' : 'Edit Profile'}
                  </button>
                </div>
              </div>
            </div>

            {/* Profile Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Profile Info Card */}
              <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                  
                  {isEditing ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Full Name</label>
                        <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Email Address</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      
                      <div className="flex justify-end space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={() => setIsEditing(false)}
                          className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Save Changes
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <div className="bg-gray-200 border-2 border-dashed rounded-xl w-12 h-12 sm:w-16 sm:h-16" />
                        <div>
                          <h4 className="text-base sm:text-lg font-medium text-gray-900">{user?.full_name || 'User'}</h4>
                          <p className="text-gray-600 text-sm">Citizen Account</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-4">
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">Full Name</p>
                          <p className="text-gray-900 text-sm sm:text-base">{user?.full_name || 'Not provided'}</p>
                        </div>
                        
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">Email Address</p>
                          <p className="text-gray-900 text-sm sm:text-base">{user?.email || 'Not provided'}</p>
                        </div>
                        
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">Phone Number</p>
                          <p className="text-gray-900 text-sm sm:text-base">{user?.phone || 'Not provided'}</p>
                        </div>
                        
                        <div>
                          <p className="text-xs sm:text-sm text-gray-500">Member Since</p>
                          <p className="text-gray-900 text-sm sm:text-base">
                            {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Not available'}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Account Settings */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Account Settings</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <div>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">Change Password</p>
                        <p className="text-xs sm:text-sm text-gray-500">Update your password regularly for security</p>
                      </div>
                      <button className="px-3 py-1.5 text-xs sm:text-sm text-blue-600 hover:text-blue-800">
                        Change
                      </button>
                    </div>
                    
                    <div className="flex justify-between items-center py-3 border-b border-gray-100">
                      <div>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">Two-Factor Authentication</p>
                        <p className="text-xs sm:text-sm text-gray-500">Add an extra layer of security to your account</p>
                      </div>
                      <button className="px-3 py-1.5 text-xs sm:text-sm text-blue-600 hover:text-blue-800">
                        Enable
                      </button>
                    </div>
                    
                    <div className="flex justify-between items-center py-3">
                      <div>
                        <p className="font-medium text-gray-900 text-sm sm:text-base">Notification Preferences</p>
                        <p className="text-xs sm:text-sm text-gray-500">Choose how you want to be notified</p>
                      </div>
                      <button className="px-3 py-1.5 text-xs sm:text-sm text-blue-600 hover:text-blue-800">
                        Configure
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar Info */}
              <div className="space-y-4 sm:space-y-6">
                {/* Account Stats */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Account Statistics</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Issues Reported</span>
                      <span className="font-semibold text-gray-900 text-sm">12</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Issues Resolved</span>
                      <span className="font-semibold text-gray-900 text-sm">8</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Response Rate</span>
                      <span className="font-semibold text-gray-900 text-sm">92%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 text-sm">Member Since</span>
                      <span className="font-semibold text-gray-900 text-sm">
                        {user?.created_at ? new Date(user.created_at).getFullYear() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Support */}
                <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Need Help?</h3>
                  <div className="space-y-3">
                    <button className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <p className="font-medium text-gray-900 text-sm sm:text-base">Contact Support</p>
                      <p className="text-xs sm:text-sm text-gray-500">Get help with your account</p>
                    </button>
                    <button className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors">
                      <p className="font-medium text-gray-900 text-sm sm:text-base">FAQ</p>
                      <p className="text-xs sm:text-sm text-gray-500">Find answers to common questions</p>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Profile;